import React, { useState, useEffect } from 'react'; 

import { handleResponse } from '../../../../_helpers/handle-response';
import { DropdownDataService } from '../../../../_services/dropdownData.service';
import CONSTANTS from '../../../../_constants/constants';
import { parseFloat2Decimal, parseFloat2DecimalHeader } from '../../../../_helpers/utils';
import classes from './SupplierWiseCurrencyPendingPage.module.css';

import Alert from '../../../UI/Alert/Alert';
import Button from '../../../UI/Button/Button';
import Loader from '../../../UI/Loader/Loader';
import Table from '../../../UI/Table/Table';
import ExportToCSV from '../../../UI/ExportToCSV/ExportToCSV';
import PageLayout from '../../../Layout/PageLayout/PageLayout';

const SupplierWiseCurrencyPendingPage = () => {

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0,10));
    const [toDate, setToDate] = useState(new Date().toISOString().slice(0,10));
    const [supplierId, setSupplierId] = useState('');
    const [operatorId, setOperatorId] = useState('');
    const [supplierData, setSupplierData] = useState([]);
    const [operatorData, setOperatorData] = useState([]);
    const [gridData, setGridData] = useState([]);
    const [totalData, setTotalData] = useState([]);
    const [pageCount, setPageCount] = useState(1);
    const [exportFileName, setExportFileName] = useState('');
    const [enableExport, setEnableExport] = useState(false);
    const [exportTotalData, setExportTotalData] = useState({});

    const tableHeaders = ['Supplier', 'Operator', 'Opening', 'Purchase', 'Received', 'Commission', 'Pending'];
    const dataFields = ['supplierName', 'operatorName', 'totalOpening', 'totalPurchase', 'receivedAmount', 'receivedCommission', 'totalPending'];
    const csvHeaders = [
        { label: 'Supplier', key: 'supplierName' },
        { label: 'Operator', key: 'operatorName' },
        { label: 'Opening', key: 'totalOpening' },
        { label: 'Purchase', key: 'totalPurchase' },
        { label: 'Received', key: 'receivedAmount' },
        { label: 'Commission', key: 'receivedCommission' },
        { label: 'Pending', key: 'totalPending' }
    ];
    const tableConfig = {
        headerAlignConfig: {
            'Supplier': 'left', 
            'Operator': 'center', 
            'Opening': 'center',
            'Purchase': 'center',
            'Received': 'center',
            'Commission': 'center',
            'Pending': 'center'
        },
        widthConfig: {
            'Supplier': 'auto', 
            'Operator': '154px', 
            'Opening': '154px',
            'Purchase': '154px',
            'Received': '154px',
            'Commission': '154px',
            'Pending': '154px'
        },
        alignConfig: {
            'supplierName': 'left',
            'operatorName': 'center',
            'totalOpening': 'right',
            'totalPurchase': 'right',
            'receivedAmount': 'right',
            'receivedCommission': 'right',
            'totalPending': 'right'
        }
    };

    useEffect(() => {
        getSupplierList();
        getOperatorList();
    }, []);

    useEffect(() => {
        setErrorMessage(null);
        setEnableExport(false);
    }, [fromDate, toDate, supplierId, operatorId]);
    
    const getSupplierList = () => {
        DropdownDataService.getSupplierList('filter').then( result => {
            if(result.data) {
                setSupplierId(result.data.results[0].supplierId);
                setSupplierData(result.data.results);
            } else {
                setErrorMessage(result);
            }
        });
    }

    const getOperatorList = () => {
        DropdownDataService.getOperatorList('filter').then( result => {
            if(result.data) {
                setOperatorId(result.data.results[0].operatorId);
                setOperatorData(result.data.results);
            } else {
                setErrorMessage(result);
            }
        });
    }

    const getReportData = () => {
        const requestOptions = {
            headers: { 
                'Token': localStorage.getItem('accessToken'),
                'Content-Type': 'application/json'
            },
        };
        let url = '';
        url = (supplierId === '-1' && operatorId === '-1') ?
                `${CONSTANTS.API_URL}Reports/SupplierwiseCurrencyPendingSummary?startDate=${fromDate}&endDate=${toDate}` :
                supplierId === '-1' ?
                    `${CONSTANTS.API_URL}Reports/SupplierwiseCurrencyPendingSummary?startDate=${fromDate}&endDate=${toDate}&operatorId=${operatorId}` :
                    operatorId === '-1' ?
                        `${CONSTANTS.API_URL}Reports/SupplierwiseCurrencyPendingSummary?startDate=${fromDate}&endDate=${toDate}&supplierId=${supplierId}` :
                        `${CONSTANTS.API_URL}Reports/SupplierwiseCurrencyPendingSummary?startDate=${fromDate}&endDate=${toDate}&supplierId=${supplierId}&operatorId=${operatorId}`
        fetch(url, requestOptions)
        .then(handleResponse)
        .then(
            (result) => {
                if(result.data) {
                    setIsLoading(false);
                    const keysToParse = ['totalOpening', 'totalPurchase', 'receivedAmount', 'receivedCommission', 'totalPending'];
                    parseFloat2Decimal(result.data.results, keysToParse);
                    setGridData(result.data.results);
                    setPageCount(Math.ceil(result.data.results.length/10));
                    let total = result.data.header;
                    const headerKeysToParse = ['openingAmount', 'purchaseAmount', 'receivedAmount', 'commission', 'pendingAmount'];
                    parseFloat2DecimalHeader(total, headerKeysToParse);
                    setTotalData([
                        '',
                        total.openingAmount,
                        total.purchaseAmount,
                        total.receivedAmount,
                        total.commission,
                        total.pendingAmount
                    ]);
                    setExportTotalData({
                        supplierName: 'Total',
                        operatorName: '',
                        totalOpening: total.openingAmount,
                        totalPurchase: total.purchaseAmount,
                        receivedAmount: total.receivedAmount,
                        receivedCommission: total.commission,
                        totalPending: total.pendingAmount
                    });
                    setEnableExport(result.data.results.length > 0 ? true : false);
                } else {
                    setIsLoading(false);
                    setErrorMessage(result.error.message);
                }
            },
            error => {
                setIsLoading(false);
                setErrorMessage(error);
            }
        );
    }

    const showClickHandler = () => {
        setIsLoading(true);
        getReportData();
    }

    const rowClickHandler = rowData => {
        let detailedPage = window.open('/pavathi/supplier-currency-pending-detailed');
        detailedPage.startDate = fromDate;
        detailedPage.endDate = toDate;
        detailedPage.supplierId = rowData.supplierId;
        detailedPage.supplierName = rowData.supplierName;
        detailedPage.operatorId = rowData.operatorId;
        detailedPage.operatorName = rowData.operatorName;
    }

    const fromDateChangeHandler = event => setFromDate(event.target.value)
    
    const toDateChangeHandler = event => setToDate(event.target.value)

    const supplierChangeHandler = event => setSupplierId(event.target.value)
    
    const operatorChangeHandler = event => setOperatorId(event.target.value)

    const getExportFileName = () => {
        let exportFilename = 'SupplierWiseCurrencyPending';
        exportFilename += '_' + fromDate;
        exportFilename += '_' + toDate;
        exportFilename += supplierId !== '-1' ? '_' + supplierData.find(data => data.supplierId == supplierId)['supplierName'] : '';
        exportFilename += operatorId !== '-1' ? '_' + operatorData.find(data => data.operatorId == operatorId)['operatorName'] : '';
        setExportFileName(exportFilename);
    }

    return (
        <>
            { isLoading && <Loader /> }
            { !isLoading && (
                <PageLayout pageTitle='Supplier Wise Currency Pending'>
                    <div className='row mb-1'>
                        <div className='col-12' style={{display: 'flex', justifyContent:'space-between', marginTop: '6px', marginBottom: '6px'}}>
                            <form className='form-inline'>
                                <div className={`form-group ${classes.inputContainer}`}>
                                    <label className='col-form-label inputLabel'>From Date : </label>
                                    <input type='date' onKeyDown={(e) => e.preventDefault()} required className='form-control inputField' value={fromDate} onChange={fromDateChangeHandler} />
                                </div>
                                <div className={`form-group ${classes.inputContainer}`}>
                                    <label className='col-form-label inputLabel'>To Date : </label>
                                    <input type='date' onKeyDown={(e) => e.preventDefault()} required className='form-control inputField' value={toDate} onChange={toDateChangeHandler} />
                                </div>
                                <div className={`form-group ${classes.inputContainer}`}>
                                    <label className='col-form-label inputLabel'>Supplier : </label>
                                    <select className='form-select customSelect' value={supplierId} aria-label=".form-select-sm example" onChange={supplierChangeHandler}>
                                        { supplierData.map( (value, index) => {
                                            return <option key={`supplier-${index}`} className='dropdownItem' value={value.supplierId}>{value.supplierName}</option>
                                        }) }
                                    </select>
                                </div>
                                <div className={`form-group ${classes.inputContainer}`}>
                                    <label className='col-form-label inputLabel'>Operator : </label>
                                    <select className='form-select customSelect' value={operatorId} aria-label=".form-select-sm example" onChange={operatorChangeHandler}>
                                        { operatorData.map( (value, index) => {
                                            return <option key={`operator-${index}`} className='dropdownItem' value={value.operatorId}>{value.operatorName}</option>
                                        }) }
                                    </select>
                                </div>
                            </form>
                            <div>
                                <Button btnClass='btn btn-primary mr-8' onClick={showClickHandler} btnText='Show' />
                                <ExportToCSV 
                                    headers={csvHeaders}
                                    exportTotalData={exportTotalData}
                                    gridData={gridData}
                                    exportFileName={exportFileName} 
                                    exportClickHandler={getExportFileName} 
                                    enableExport={enableExport}
                                />
                            </div>
                        </div>
                    </div>
                    { errorMessage && <Alert alertClassName='danger' message={errorMessage} /> }
                    <Table 
                        tableHeaders={tableHeaders} 
                        dataFields={dataFields} 
                        tableConfig={tableConfig}
                        pageCount={pageCount} 
                        sort={false}
                        actions={true} 
                        view={true}
                        paginate={true}
                        filter={false}
                        gridData={gridData}
                        totalData={totalData}
                        onRowClick={rowClickHandler}
                    />
                </PageLayout>
            )}    
        </>
    );
  
}

export default SupplierWiseCurrencyPendingPage;